import TextField from '@mui/material/TextField'
import { useState } from 'react'
import { Column } from 'react-table'
import {
  IDaySetting,
  IDaySettingPartial,
} from '../../../../../../../app/entities/DaySetting'
import Table from '../../../../../global/components/table/Table'
import TableTitle from '../../../../../global/components/table/helpers/table-title/TableTitle'
import TableTopper from '../../../../../global/components/table/helpers/table-topper/TableTopper'
import { fetchData } from '../../../../../global/utils/fetch'
import { updateDaySetting } from '../../api'
import { WorkDayDropDown } from '../custom-inputs/CustomInputs'
import AddUniqueDay from './AddUniqueDay'
import DaySettingActivityLog from './activity-log/DaySettingActivityLog'
import DeleteDaySettingConfirmation from './delete-confirmation/DeleteDaySettingConfirmation'
import styles from './styles.module.css'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'
import { formatDateYyyyMmDd } from '../../../../../global/utils/date/date-utils-yyyy-mm-dd'

interface IUniqueDayTableProps {
  technicianData: ITechnicianPartial
  isDefaultWorkDay: boolean
  isDefaultLunch: boolean
  refetchTechnicianData: () => void
  technicianObjectId: string | undefined
}

const nonWorkingDayTextFieldStyles = {
  width: 130,
}
const workingDayTextFieldStyles = {
  width: 130,
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'black',
  },
}

export default function UniqueDaySettingsTable({
  technicianData,
  isDefaultLunch,
  isDefaultWorkDay,
  refetchTechnicianData,
  technicianObjectId,
}: IUniqueDayTableProps) {
  const { daySettings } = technicianData

  const [editingRowIndex, setEditingRowIndex] = useState(-1)
  const [newDaySettingElement, setNewDaySettingElement] =
    useState<JSX.Element | null>(null)
  const [editedDaySetting, setEditedDaySetting] = useState<IDaySetting | null>(
    null,
  )
  const [activityLogDaySetting, setActivityLogDaySetting] =
    useState<IDaySetting | null>(null)

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false)
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false)

  // Add new day settings
  function cancelAddNewDaySetting() {
    return setNewDaySettingElement(null)
  }
  function addNewDaySetting() {
    return setNewDaySettingElement(
      <AddUniqueDay
        cancel={cancelAddNewDaySetting}
        technicianData={technicianData}
        isDefaultWorkDay={isDefaultWorkDay}
        isDefaultLunch={isDefaultLunch}
        refetchTechnicianData={refetchTechnicianData}
        technicianObjectId={technicianObjectId}
      />,
    )
  }

  // Edit day settings
  function handleEditDaySetting(
    newValue: string | boolean,
    valueName: string,
    rowIndex: number,
  ) {
    const canEditDaySetting = rowIndex === editingRowIndex && editedDaySetting
    if (canEditDaySetting) {
      setEditedDaySetting({ ...editedDaySetting, [valueName]: newValue })
    }
  }
  function handleEditRow(rowIndex: number) {
    setEditingRowIndex(rowIndex)
    const daySettingToEdit: IDaySetting | null = daySettings
      ? daySettings[rowIndex]
      : null
    setEditedDaySetting(daySettingToEdit)
  }
  function getDaySettingValue(
    value: any,
    name: keyof IDaySetting,
    rowIndex: number,
  ) {
    let returnValue = value
    const getEditDaySettingValue =
      rowIndex === editingRowIndex && editedDaySetting
    if (getEditDaySettingValue) {
      returnValue = editedDaySetting[name]
    }
    if (name === 'day') {
      const dateDay = new Date(returnValue)
      return formatDateYyyyMmDd(dateDay)
    } else {
      return returnValue
    }
  }
  // Remove day settings
  function handleRemoveRow(rowIndex: number) {
    const daySettingToRemove: IDaySetting | null = daySettings
      ? daySettings[rowIndex]
      : null
    setEditedDaySetting(daySettingToRemove)
    setIsDeleteConfirmationModalOpen(true)
    setEditingRowIndex(-1)
  }
  function handleCloseRemoveConfirmationModal() {
    setIsDeleteConfirmationModalOpen(false)
    setEditedDaySetting(null)
  }

  // Cancel editing day settings
  function handleCancelRow() {
    setEditedDaySetting(null)
    setEditingRowIndex(-1)
  }

  // Add day setting changes to technician
  async function handleSaveEditRow(rowIndex: number) {
    const canSaveDaySetting = rowIndex === editingRowIndex && editedDaySetting
    if (!canSaveDaySetting) return
    try {
      await fetchData(updateDaySetting(editedDaySetting, technicianObjectId))
      if (refetchTechnicianData) {
        refetchTechnicianData()
      } else {
        alert('Technician must be created in order to add day settings.')
      }
    } catch (err) {
      alert(`Error updating day setting: ${err}`)
    }
    setEditingRowIndex(-1)
  }

  function getWorkDayDependentStyling(
    workDay: boolean,
    isEditingThisRow: boolean,
  ) {
    return workDay && isEditingThisRow
      ? workingDayTextFieldStyles
      : nonWorkingDayTextFieldStyles
  }

  const columns: Column<IDaySetting>[] = [
    {
      Header: 'Date',
      accessor: (data: IDaySettingPartial) => {
        const { day } = data
        return day
      },
      Cell: ({ value, row: { index } }: any) => {
        const isEditingRow = index === editingRowIndex
        return (
          <TextField
            onChange={(event) =>
              handleEditDaySetting(event.target.value, event.target.name, index)
            }
            value={getDaySettingValue(value, 'day', index)}
            InputLabelProps={{ shrink: true }}
            type='Date'
            label='Date'
            name='day'
            disabled={!isEditingRow}
            sx={{
              width: 125,
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'black',
              },
            }}
          />
        )
      },
    },
    {
      Header: 'Work Day',
      accessor: (data: IDaySettingPartial, rowIndex: number) => {
        const { workDay } = data
        const isEditingRow = rowIndex === editingRowIndex
        return (
          <WorkDayDropDown
            disabled={!isEditingRow}
            value={getDaySettingValue(workDay, 'workDay', rowIndex)}
            onChange={(newValue: boolean) =>
              handleEditDaySetting(newValue, 'workDay', rowIndex)
            }
          />
        )
      },
    },
    {
      Header: 'Reason',
      accessor: (data: IDaySettingPartial, rowIndex: number) => {
        const { reason } = data
        const isEditingRow = rowIndex === editingRowIndex
        return (
          <TextField
            value={getDaySettingValue(reason, 'reason', rowIndex)}
            label='Reason'
            name='reason'
            disabled={!isEditingRow}
            InputLabelProps={{ shrink: true }}
            sx={workingDayTextFieldStyles}
            onChange={(event) =>
              handleEditDaySetting(
                event.target.value,
                event.target.name,
                rowIndex,
              )
            }
          />
        )
      },
    },
    {
      Header: 'Working Hours',
      accessor: (daySetting: IDaySettingPartial, rowIndex: number) => {
        const { startTime, endTime } = daySetting
        const isWorkingDay = editedDaySetting?.workDay
        let workingHoursAreEnabled = false
        const isEditingRow = rowIndex === editingRowIndex
        const workDayDependentStyling = getWorkDayDependentStyling(
          isWorkingDay ?? false,
          isEditingRow,
        )
        if (isEditingRow) {
          workingHoursAreEnabled = editedDaySetting?.workDay ?? false
        }

        return (
          <div style={{ display: 'flex', gap: '4px' }}>
            <TextField
              type='time'
              label='From'
              name='startTime'
              sx={{ ...workDayDependentStyling, width: 120 }}
              value={getDaySettingValue(startTime, 'startTime', rowIndex)}
              disabled={!workingHoursAreEnabled}
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                handleEditDaySetting(
                  event.target.value,
                  event.target.name,
                  rowIndex,
                )
              }
            />
            <TextField
              type='time'
              label='To'
              name='endTime'
              sx={{ ...workDayDependentStyling, width: 120 }}
              value={getDaySettingValue(endTime, 'endTime', rowIndex)}
              disabled={!workingHoursAreEnabled}
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                handleEditDaySetting(
                  event.target.value,
                  event.target.name,
                  rowIndex,
                )
              }
            />
          </div>
        )
      },
    },
    {
      Header: 'Lunch Duration',
      accessor: (data: IDaySettingPartial, rowIndex: number) => {
        const { lunchDuration } = data
        const isWorkingDay = editedDaySetting?.workDay
        const isEditingRow = rowIndex === editingRowIndex
        const workDayDependentStyling = getWorkDayDependentStyling(
          isWorkingDay ?? false,
          isEditingRow,
        )

        return (
          <div>
            <TextField
              type='number'
              label='Lunch Duration'
              name='lunchDuration'
              sx={{ ...workDayDependentStyling, width: 120 }}
              value={getDaySettingValue(
                lunchDuration,
                'lunchDuration',
                rowIndex,
              )}
              disabled={!isEditingRow || !isWorkingDay}
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                handleEditDaySetting(
                  event.target.value,
                  event.target.name,
                  rowIndex,
                )
              }
            />
          </div>
        )
      },
    },
    {
      Header: ' ',
      accessor: (daySetting: IDaySetting, rowIndex: number) => {
        const isEditingRow = rowIndex === editingRowIndex
        const hasActivityLog =
          daySetting.activityLog && daySetting.activityLog.length > 0

        return (
          <div className={styles.buttonCell}>
            <a
              className={styles.buttonText}
              onClick={() =>
                isEditingRow ? handleCancelRow() : handleRemoveRow(rowIndex)
              }
            >
              {isEditingRow ? 'Cancel' : 'Remove'}
            </a>
            <a
              className={styles.buttonText}
              onClick={() =>
                isEditingRow
                  ? handleSaveEditRow(rowIndex)
                  : handleEditRow(rowIndex)
              }
            >
              {isEditingRow ? 'Save' : 'Edit'}
            </a>
            {hasActivityLog && (
              <a
                className={styles.buttonText}
                onClick={() => {
                  setActivityLogDaySetting(daySetting)
                  setIsActivityLogOpen(true)
                }}
              >
                View Log
              </a>
            )}
          </div>
        )
      },
    },
  ]
  return (
    <div style={{ width: '100%' }}>
      <DaySettingActivityLog
        isOpen={isActivityLogOpen}
        handleClosed={() => setIsActivityLogOpen(false)}
        daySetting={activityLogDaySetting}
      />
      <TableTopper>
        <div className={styles.titleButtonContainer}>
          <DeleteDaySettingConfirmation
            isOpen={isDeleteConfirmationModalOpen}
            techId={technicianObjectId}
            handleClosed={handleCloseRemoveConfirmationModal}
            daySetting={editedDaySetting}
            reloadModal={refetchTechnicianData}
          />
          <TableTitle>Unique Day Settings Table</TableTitle>
          <button
            onClick={addNewDaySetting}
            style={{
              all: 'unset',
              cursor: !technicianObjectId ? 'not-allowed' : 'pointer',
              color: !technicianObjectId ? '#999' : '#d70000',
            }}
            disabled={!technicianObjectId}
          >
            Add New
          </button>
        </div>
      </TableTopper>
      <div style={{ width: '100%' }}>
        <Table
          columns={columns}
          data={
            (daySettings
              ? daySettings.sort(
                  (a, b) =>
                    new Date(b.day).getTime() - new Date(a.day).getTime(),
                )
              : []) as any
          }
          isDisplayMode={true}
          isLoading={false}
          customRow={newDaySettingElement}
        />
      </div>
    </div>
  )
}
